export const getQuestionFlow = (type) => {
  switch (type) {
    case 'partnership':
      return partnershipFlow;
    case 'contractor':
      return contractorFlow;
    case 'employment':
      return employmentFlow;
    case 'real_estate':
      return realEstateFlow;
    default:
      return partnershipFlow;
  }
};

const partnershipFlow = [
  {
    id: 'party_details',
    title: 'Who are the partners?',
    description: 'List the full legal names and addresses of all partners involved in this agreement.',
    type: 'textarea',
    required: true,
    placeholder: 'Partner 1: John Doe, 123 Main St...\nPartner 2: Jane Smith, 456 Oak Ave...',
    aiSuggestion: 'Ensure you include full legal names as they appear on government ID. If partners are entities (like other companies), use the full registered entity name.'
  },
  {
    id: 'business_details',
    title: 'What is the business purpose?',
    description: 'Describe the nature of the business and its primary activities.',
    type: 'textarea',
    required: true,
    placeholder: 'The partnership is formed to develop and sell software solutions for...',
    aiSuggestion: 'Be specific but broad enough to allow for reasonable business pivots without needing to amend the agreement immediately.'
  },
  {
    id: 'equity_split',
    title: 'How will equity be split?',
    description: 'Define the ownership percentage for each partner.',
    type: 'textarea',
    required: true,
    placeholder: 'John Doe: 50%\nJane Smith: 50%',
    aiSuggestion: '50/50 splits can lead to deadlocks. Consider a 51/49 split or a mechanism for breaking ties, like an independent advisor.'
  },
  {
    id: 'initial_capital',
    title: 'Initial Capital Contributions',
    description: 'What are partners contributing to start the business (money, assets, IP)?',
    type: 'textarea',
    required: false,
    placeholder: 'Partner 1: $10,000 cash\nPartner 2: Intellectual Property (Codebase)',
    aiSuggestion: 'Documenting non-cash contributions (like IP or equipment) is crucial to avoid disputes about value later.'
  },
  {
    id: 'roles_responsibilities',
    title: 'Roles and Responsibilities',
    description: 'What is expected of each partner?',
    type: 'textarea',
    required: true,
    placeholder: 'Partner 1: CEO, handling sales and operations\nPartner 2: CTO, handling product development',
    aiSuggestion: 'Clear role definitions help prevent "too many cooks" scenarios. Define who has final say in their respective domains.'
  },
  {
    id: 'compensation',
    title: 'How will partners be compensated?',
    description: 'Will partners receive a salary or just profit distributions?',
    type: 'radio',
    required: true,
    options: [
      { value: 'none', label: 'No salary initially (profit share only)', description: 'Common for early stage startups' },
      { value: 'equal', label: 'Equal salaries', description: 'Partners paid same amount' },
      { value: 'varying', label: 'Varying salaries based on role', description: 'Pay based on market rate for role' }
    ]
  },
  {
    id: 'decision_making',
    title: 'How are major decisions made?',
    description: 'What vote threshold is needed for key business decisions?',
    type: 'radio',
    required: true,
    options: [
      { value: 'unanimous', label: 'Unanimous Consent', description: 'All partners must agree' },
      { value: 'majority', label: 'Simple Majority (>50%)', description: 'Majority of ownership interest' },
      { value: 'proportional', label: 'Proportional to Equity', description: 'Vote weight equals equity stake' }
    ]
  },
  {
    id: 'vesting_schedule',
    title: 'Equity Vesting Schedule',
    description: 'How do partners earn their equity over time?',
    type: 'radio',
    required: true,
    options: [
      { value: 'standard', label: 'Standard 4-Year Vesting', description: '1 year cliff, monthly thereafter' },
      { value: 'none', label: 'No Vesting (Immediate)', description: 'Full ownership immediately' },
      { value: 'custom', label: 'Custom Schedule', description: 'Define your own terms' }
    ],
    aiSuggestion: 'We strongly recommend standard vesting. It protects the business if a co-founder leaves early.'
  },
  {
    id: 'exit_clauses',
    title: 'Exit Strategy (Buy-Sell)',
    description: 'What happens if a partner wants to leave or sell their share?',
    type: 'textarea',
    required: true,
    placeholder: 'Right of first refusal for remaining partners. Valuation based on...',
    aiSuggestion: 'Include a "Right of First Refusal" so remaining partners can buy the leaving partner\'s shares before they are sold to an outsider.'
  },
  {
    id: 'ip_ownership',
    title: 'Intellectual Property',
    description: 'Who owns the IP created for the business?',
    type: 'radio',
    required: true,
    options: [
      { value: 'company', label: 'Company Owned', description: 'All IP assigned to partnership' },
      { value: 'joint', label: 'Jointly Owned', description: 'Partners own IP individually' },
      { value: 'other', label: 'Other Arrangement', description: 'Specific licensing terms' }
    ],
    warning: 'Investors almost always require the Company to own all IP.'
  },
  {
    id: 'dissolution',
    title: 'Dissolution Terms',
    description: 'How can the partnership be dissolved?',
    type: 'radio',
    required: true,
    options: [
      { value: 'unanimous', label: 'Unanimous Vote', description: 'All must agree to close' },
      { value: 'supermajority', label: 'Supermajority (75%)', description: 'High threshold for closure' },
      { value: 'majority', label: 'Simple Majority', description: '51% can vote to dissolve' }
    ]
  }
];

const contractorFlow = [
  {
    id: 'parties_info',
    title: 'Party Information',
    description: 'Details of the Client and the Contractor.',
    type: 'textarea',
    required: true,
    placeholder: 'Client: Company Name, Address...\nContractor: Name, Address...',
  },
  {
    id: 'scope_of_work',
    title: 'Scope of Work',
    description: 'Detailed description of services to be provided.',
    type: 'textarea',
    required: true,
    placeholder: 'Contractor will provide the following services...',
    aiSuggestion: 'Be as specific as possible to avoid scope creep. List specific deliverables.'
  },
  {
    id: 'timeline',
    title: 'Project Timeline',
    description: 'Start date, end date, and key milestones.',
    type: 'textarea',
    required: true,
    placeholder: 'Start Date: Jan 1, 2024\nCompletion Date: March 31, 2024\nMilestones...',
  },
  {
    id: 'compensation_structure',
    title: 'Compensation Structure',
    description: 'How will the contractor be paid?',
    type: 'radio',
    required: true,
    options: [
      { value: 'fixed', label: 'Fixed Project Fee', description: 'One set price for the work' },
      { value: 'hourly', label: 'Hourly Rate', description: 'Pay for time spent' },
      { value: 'milestone', label: 'Milestone Based', description: 'Pay upon completing steps' },
      { value: 'retainer', label: 'Monthly Retainer', description: 'Recurring fixed fee' }
    ]
  },
  {
    id: 'payment_terms',
    title: 'Payment Terms',
    description: 'When constitute payment due dates (e.g., Net 15, Net 30)?',
    type: 'textarea',
    required: true,
    placeholder: 'Invoices sent monthly, payable within 15 days...',
  },
  {
    id: 'expenses',
    title: 'Expense Reimbursement',
    description: 'Will the client reimburse expenses?',
    type: 'radio',
    required: true,
    options: [
      { value: 'none', label: 'No Reimbursement', description: 'Contractor covers own costs' },
      { value: 'preapproved', label: 'Pre-approved Only', description: 'Must get approval first' },
      { value: 'other', label: 'Custom Terms', description: 'Specific arrangement' }
    ]
  },
  {
    id: 'confidentiality',
    title: 'Confidentiality Required?',
    description: 'Does the contractor need to keep information private?',
    type: 'radio',
    required: true,
    options: [
      { value: 'yes', label: 'Yes, NDA terms included', description: 'Strict confidentiality' },
      { value: 'no', label: 'No specific requirement', description: 'Standard professional care' }
    ]
  },
  {
    id: 'ip_rights',
    title: 'Intellectual Property Rights',
    description: 'Who owns the work produced?',
    type: 'radio',
    required: true,
    options: [
      { value: 'client', label: 'Client (Work for Hire)', description: 'Client owns everything' },
      { value: 'contractor', label: 'Contractor Retains', description: 'Client gets license to use' },
      { value: 'other', label: 'Mixed Ownership', description: 'Specify details' }
    ],
    warning: 'For software and creative work, "Work for Hire" is standard for clients.'
  },
  {
    id: 'termination',
    title: 'Termination Clause',
    description: 'How can the agreement be ended early?',
    type: 'textarea',
    required: true,
    placeholder: 'Either party may terminate with 14 days written notice...',
  },
  {
    id: 'additional_contractor_terms',
    title: 'Additional Terms',
    description: 'Any other specific clauses?',
    type: 'textarea',
    required: false,
    placeholder: 'Non-solicitation of employees, equipment requirements...',
  }
];

const employmentFlow = [
  {
    id: 'employer_employee_info',
    title: 'Employer & Employee Details',
    description: 'Names and addresses for the agreement.',
    type: 'textarea',
    required: true,
    placeholder: 'Employer: Company Name, Address\nEmployee: Full Name, Address',
  },
  {
    id: 'job_details',
    title: 'Job Title and Description',
    description: 'Position title and key responsibilities.',
    type: 'textarea',
    required: true,
    placeholder: 'Title: Senior Developer\nReports to: CTO\nResponsibilities: ...',
  },
  {
    id: 'compensation_details',
    title: 'Compensation Package',
    description: 'Salary, bonus potential, and pay frequency.',
    type: 'textarea',
    required: true,
    placeholder: 'Annual Salary: $100,000\nPay Period: Bi-weekly\nBonus: Up to 10% based on performance',
  },
  {
    id: 'employment_type',
    title: 'Employment Type',
    description: 'Structure of the employment relationship.',
    type: 'radio',
    required: true,
    options: [
      { value: 'fulltime_permanent', label: 'Full-Time Permanent', description: 'Standard 40hr/week' },
      { value: 'parttime_permanent', label: 'Part-Time Permanent', description: '<30hr/week' },
      { value: 'contract', label: 'Fixed Term Contract', description: 'Ends on specific date' }
    ]
  },
  {
    id: 'at_will_employment',
    title: 'At-Will Employment?',
    description: 'Can employment be terminated by either party at any time?',
    type: 'radio',
    required: true,
    options: [
      { value: 'yes', label: 'Yes (Standard in US)', description: 'No fixed term' },
      { value: 'no', label: 'No (Fixed Term)', description: 'Contract for specific duration' }
    ]
  },
  {
    id: 'benefits_package',
    title: 'Benefits',
    description: 'Health insurance, PTO, and other perks.',
    type: 'textarea',
    required: true,
    placeholder: 'Health/Dental/Vision, 15 days PTO, 401k match...',
  },
  {
    id: 'probationary_period',
    title: 'Probationary Period',
    description: 'Is there an initial trial period?',
    type: 'radio',
    required: true,
    options: [
      { value: 'none', label: 'No Probation', description: 'Full status immediately' },
      { value: '90_days', label: '90 Days', description: 'Standard trial period' },
      { value: 'custom', label: 'Custom Period', description: 'Specify in notes' }
    ]
  },
  {
    id: 'confidentiality_ip',
    title: 'Confidentiality & IP Assignment',
    description: 'Require NDA and IP assignment to company?',
    type: 'radio',
    required: true,
    options: [
      { value: 'yes', label: 'Yes (Highly Recommended)', description: 'Protects company assets' },
      { value: 'standard', label: 'Standard Clauses Only', description: 'Basic protection' }
    ]
  },
  {
    id: 'non_compete_terms',
    title: 'Restrictive Covenants',
    description: 'Non-compete and non-solicitation terms.',
    type: 'radio',
    required: true,
    options: [
      { value: 'none', label: 'None', description: 'No restrictions' },
      { value: 'non_solicit', label: 'Non-Solicit Only', description: 'Cannot poach clients/staff' },
      { value: 'detailed', label: 'Non-Compete & Non-Solicit', description: 'Check local laws validity' }
    ],
    warning: 'Non-compete agreements are banned or restricted in many states (e.g., CA, NY). Check local laws.'
  },
  {
    id: 'termination_severance',
    title: 'Termination & Severance',
    description: 'Notice periods and severance pay policies.',
    type: 'textarea',
    required: false,
    placeholder: 'Notice: 2 weeks\nSeverance: 1 week pay per year of service...',
  },
  {
    id: 'additional_employment_terms',
    title: 'Additional Terms',
    description: 'Any other specific conditions?',
    type: 'textarea',
    required: false,
    placeholder: 'Remote work policy, equipment provision...',
  }
];

const realEstateFlow = [
  {
    id: 'property_type',
    title: 'What type of real estate transaction is this?',
    description: 'Specify whether this is for leasing, purchasing, or managing property.',
    type: 'radio',
    required: true,
    options: [
      { value: 'residential_lease', label: 'Residential Lease', description: 'Renting a home or apartment' },
      { value: 'commercial_lease', label: 'Commercial Lease', description: 'Office or retail space leasing' },
      { value: 'property_purchase', label: 'Property Purchase', description: 'Buying real estate' },
      { value: 'property_management', label: 'Property Management', description: 'Management services agreement' },
      { value: 'sublease', label: 'Sublease Agreement', description: 'Subletting premises' }
    ],
    aiSuggestion: 'Different property types have different legal requirements and risk considerations. Choose the category that best fits your situation.'
  },
  {
    id: 'parties_info',
    title: 'Provide information about all parties involved',
    description: 'Include details for landlord/owner and tenant/buyer as applicable.',
    type: 'textarea',
    required: true,
    placeholder: 'Landlord: ABC Property Management, 123 Main St, City, State 12345\nTenant: John Buyer, 456 Oak Ave, City, State 67890\nContact Info: john@email.com, (555) 123-4567',
    helpText: 'Include full legal names, addresses, and contact information for all parties.'
  },
  {
    id: 'property_details',
    title: 'Describe the property and its location',
    description: 'Provide detailed information about the property being leased or sold.',
    type: 'textarea',
    required: true,
    placeholder: 'Property Address: 789 Elm Street, Springfield, IL 62701\nType: Single-family home\nSize: 2,500 sq ft, 3 bedrooms, 2 bathrooms\nFeatures: Garage, backyard, hardwood floors\nZoning: Residential',
    helpText: 'Be specific about address, size, condition, and any special features or restrictions.'
  },
  {
    id: 'financial_terms',
    title: 'What are the key financial terms?',
    description: 'Specify rent/purchase price, deposits, payment schedules, and other costs.',
    type: 'textarea',
    required: true,
    placeholder: 'Monthly Rent: $2,500\nSecurity Deposit: $2,500 (refundable)\nFirst Month\'s Rent: Due upon signing\nLate Fees: $50 after 5th of month\nUtilities: Tenant responsible for electricity, gas, water\nParking: One covered space included',
    aiSuggestion: 'Financial terms vary significantly by location and property type. Research local market rates and include all fees, deposits, and payment schedules.'
  },
  {
    id: 'lease_term',
    title: 'What is the term or duration?',
    description: 'Specify how long the agreement lasts and any renewal options.',
    type: 'textarea',
    required: true,
    placeholder: 'Initial Term: 12 months\nCommencement Date: March 1, 2024\nExpiration Date: February 28, 2025\nRenewal Options: Automatic renewal for 6 months unless 60 days notice given\nEarly Termination: Allowed with 30 days notice and $500 fee',
    helpText: 'Include start date, end date, renewal terms, and any early termination provisions.'
  },
  {
    id: 'responsibilities',
    title: 'Who is responsible for maintenance and repairs?',
    description: 'Define responsibilities for upkeep, repairs, and property maintenance.',
    type: 'textarea',
    required: true,
    placeholder: 'Landlord Responsibilities:\n- Structural repairs and maintenance\n- Roof, foundation, plumbing (major)\n- Lawn care and landscaping\n- Appliance repairs (built-in)\n\nTenant Responsibilities:\n- Normal wear and tear\n- Minor repairs under $100\n- Cleaning and upkeep\n- Personal belongings and modifications',
    aiSuggestion: 'Clearly separating maintenance responsibilities prevents disputes. Landlords typically handle structural issues while tenants handle day-to-day maintenance.'
  },
  {
    id: 'use_restrictions',
    title: 'Are there any restrictions on property use?',
    description: 'Specify allowed uses, prohibited activities, and occupancy limits.',
    type: 'textarea',
    required: false,
    placeholder: 'Permitted Use: Residential only, no business activity\nOccupancy: Maximum 4 occupants\nPets: Up to 2 pets allowed with $300 deposit each\nSmoking: No smoking inside property\nSubletting: Not permitted without landlord approval',
    helpText: 'Common restrictions include occupancy limits, pet policies, and business use prohibitions. Check local laws about what restrictions are legally enforceable.'
  },
  {
    id: 'insurance_requirements',
    title: 'What insurance is required?',
    description: 'Specify insurance requirements for property damage and liability.',
    type: 'textarea',
    required: true,
    placeholder: 'Tenant Insurance: Required renter\'s insurance with minimum $100,000 liability coverage\nLandlord Insurance: Maintains property insurance for building and liability\nFlood Insurance: Required if property is in flood zone\nDeductibles: Tenant responsible for their deductible amounts',
    aiSuggestion: 'Insurance requirements protect both parties. Landlords typically require tenant insurance for personal property and liability, while maintaining coverage for the building itself.'
  },
  {
    id: 'termination_conditions',
    title: 'Under what conditions can the agreement be terminated?',
    description: 'Define grounds for early termination and required notice periods.',
    type: 'textarea',
    required: true,
    placeholder: 'Tenant Termination: 30 days written notice, no early termination fee\nLandlord Termination: 60 days notice for month-to-month, cause required (non-payment, property damage, illegal activity)\nEviction Process: Follow local eviction laws\nSale of Property: Right to terminate with 90 days notice if property sells',
    helpText: 'Termination terms must comply with local landlord-tenant laws. Research your area\'s specific requirements for notice periods and valid termination grounds.'
  },
  {
    id: 'additional_provisions',
    title: 'Any additional terms or special provisions?',
    description: 'Include any other important terms specific to this property or situation.',
    type: 'textarea',
    required: false,
    placeholder: 'Examples:\n- Parking space assignment\n- Storage unit access\n- Community rules and HOA fees\n- Utilities setup and transfer\n- Key deposits and return\n- Inspection rights\n- Lead paint disclosure (if applicable)',
    helpText: 'Include any unique aspects of this property or local requirements. Common additions include parking details, utility arrangements, and inspection rights.'
  }
];